import React, {useEffect, useState} from "react";
import {NewPromptInput} from "common_components/llm_chat/new_prompt_input";
import {
  ChatContainer,
  TitleBar,
  ChatContent,
} from "common_components/llm_chat/styled";
import {NewQueryButton} from "common_components/llm_chat/new_query_button";
import {ConversationId, DocumentConversation} from "common/types/conversation";
import {removeIssueGroupPrefix} from "common/utils/issues/remove_issue_group_prefix";
import _ from "lodash";
import {User} from "common/types/user";
import {Grid, IconButton} from "@material-ui/core";
import QueryHistoryFilters from "./query_history_filters";
import FilterListIcon from "@material-ui/icons/FilterList";
import CloseIcon from "@material-ui/icons/Close";
import {getMessageUsername} from "./util/get_message_username";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

export interface ChatHistoryProps {
  chats: {[key: ConversationId]: DocumentConversation};
  selectedConversation: ConversationId;
  setSelectedConversation: (conversation: ConversationId | null) => void;
  onNewChat: () => void;
  generatePrompt: () => void;
  prompt: string;
  setPrompt: (prompt: string) => void;
  isLoading: boolean;
  textFieldRef: React.MutableRefObject<HTMLInputElement | null>;
  currentUser: User;
  onClose;
  canMakeQueries;
  allowBackwardsNavigation: boolean;
  chatType: "issue_chat" | "document_chat";
  highlightedConversationId?: number | null;
  compact?: boolean;
}

const styles = {
  chatList: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    rowGap: "1rem",
    padding: "1rem",
    backgroundColor: "rgb(230, 230, 230)",
  },
  compactChatList: {
    rowGap: "0.5rem",
    padding: "0.5rem",
  },
  conversationCard: {
    cursor: "pointer",
    padding: "0.6rem",
    borderRadius: "0.5rem",
    fontFamily: "Roboto",
  },
  compactConversationCard: {
    padding: "0.4rem 0.5rem",
    borderRadius: "0.375rem",
  },
  username: {
    color: "rgb(83, 83, 83)",
    fontSize: "0.7rem",
  },
  conversationText: {
    fontWeight: "bold",
    fontSize: "0.85rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    paddingBottom: "0.2rem",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    color: "rgb(83, 83, 83)",
    fontSize: "0.7rem",
  },
  issueNameBadge: {
    border: "1px solid rgb(180, 206, 240)",
    color: "rgb(25, 103, 210)",
    borderRadius: "0.25rem",
    padding: "0.1rem 0.4rem",
    fontSize: "0.65rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "60%",
  },
} as const;

export function ChatHistory({
  chats,
  selectedConversation,
  setSelectedConversation,
  onNewChat,
  generatePrompt,
  prompt,
  setPrompt,
  isLoading,
  textFieldRef,
  onClose,
  canMakeQueries,
  allowBackwardsNavigation = true,
  currentUser,
  chatType,
  highlightedConversationId,
  compact = false,
}: ChatHistoryProps) {
  const users = getUsersFromChats(chats);
  const userIds = users.map(user => user.id);
  const [shownUserIds, setShownUserIds] = useState(userIds);
  const [shownChats, setShownChats] = useState(chats);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const users = getUsersFromChats(chats);
    const userIds = users.map(user => user.id);
    setShownUserIds(userIds);
    setShownChats(chats);
  }, [chats]);

  const onShownUserIdsChange = userIds => {
    setShownUserIds(userIds);
    const filteredChats = Object.fromEntries(
      Object.entries(chats).filter(([, chat]) =>
        userIds.includes(chat.first_user.id),
      ),
    );
    setShownChats(filteredChats);
  };

  const onShowOnlyCurrentUserChats = () => {
    if (_.isEqual(shownUserIds, [currentUser.id])) {
      onShownUserIdsChange(userIds);
      return;
    }
    onShownUserIdsChange([currentUser.id]);
  };

  return (
    <ChatContainer>
      <TitleBar $compact={compact}>
        <div style={{display: "flex", alignItems: "center"}}>
          {allowBackwardsNavigation && chatType !== "issue_chat" && (
            <IconButton
              edge="start"
              color="inherit"
              size={compact ? "small" : "medium"}
              onClick={onClose}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
          Query History
        </div>
        <div style={{display: "flex", alignItems: "center"}}>
          <IconButton
            style={{color: "white"}}
            size={compact ? "small" : "medium"}
            title="Filter queries"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? <CloseIcon /> : <FilterListIcon />}
          </IconButton>
          {canMakeQueries && (
            <NewQueryButton onNewChat={onNewChat} compact={compact} />
          )}
        </div>
      </TitleBar>
      {showFilters && (
        <QueryHistoryFilters
          currentUser={currentUser}
          shownUserIds={shownUserIds}
          onShownUserIdsChange={onShownUserIdsChange}
          onShowOnlyCurrentUserChats={onShowOnlyCurrentUserChats}
          users={users}
        />
      )}
      <ChatContent>
        {renderChatHistoryItems(
          shownChats,
          selectedConversation,
          setSelectedConversation,
          currentUser,
          highlightedConversationId,
          compact,
        )}
      </ChatContent>

      {/* padding compensates for MUI grid -4px margin causing overflows */}
      {canMakeQueries && compact ? (
        <div style={{padding: "6px 8px"}}>
          <NewPromptInput
            prompt={prompt}
            setPrompt={setPrompt}
            generateLlmPrompt={generatePrompt}
            isLoading={isLoading}
            textFieldRef={textFieldRef}
            compact
          />
        </div>
      ) : canMakeQueries ? (
        <div style={{padding: "4px"}}>
          <Grid container spacing={1} style={{padding: "0.5em"}}>
            <NewPromptInput
              prompt={prompt}
              setPrompt={setPrompt}
              generateLlmPrompt={generatePrompt}
              isLoading={isLoading}
              textFieldRef={textFieldRef}
            />
          </Grid>
        </div>
      ) : (
        <div style={{textAlign: "center", padding: "1em"}}>
          <i style={{color: "grey"}}>
            You do not have permission to make new conversations, please contact
            an administrator
          </i>
        </div>
      )}
    </ChatContainer>
  );
}

function renderChatHistoryItems(
  chats,
  selectedConversation,
  setSelectedConversation,
  currentUser,
  highlightedConversationId?: number | null,
  compact = false,
) {
  return (
    <div
      style={{...styles.chatList, ...(compact ? styles.compactChatList : {})}}
    >
      {Object.keys(chats)
        .sort(
          (a, b) =>
            (!chats[b].creation_date ? 1 : 0) -
              (!chats[a].creation_date ? 1 : 0) ||
            new Date(chats[b].creation_date).valueOf() -
              new Date(chats[a].creation_date).valueOf(),
        )
        .map(conversationId =>
          renderConversationSummary(
            chats[conversationId],
            conversationId,
            selectedConversation,
            setSelectedConversation,
            currentUser,
            highlightedConversationId,
            compact,
          ),
        )}
    </div>
  );
}

function renderConversationSummary(
  conversation,
  index,
  selectedConversation,
  setSelectedConversation,
  currentUser,
  highlightedConversationId?: number | null,
  compact = false,
) {
  const isSelected = selectedConversation?.id === conversation.id;
  const isHighlighted =
    !isSelected && highlightedConversationId === Number(conversation.id);
  return (
    <div
      key={index}
      onClick={() => setSelectedConversation(conversation)}
      style={{
        ...styles.conversationCard,
        ...(compact ? styles.compactConversationCard : {}),
        backgroundColor: isHighlighted ? "#f0f7ff" : "#fdfdfd",
        border: `${compact ? "2px" : "0.3rem"} solid ${
          isSelected
            ? "rgb(188, 217, 240)"
            : isHighlighted
            ? "rgb(220, 235, 247)"
            : "transparent"
        }`,
      }}
    >
      <div style={{display: "flex", alignItems: "center"}}>
        <div style={{flex: 1, minWidth: 0}}>
          <div style={styles.username}>
            {renderConversationUsername(conversation, currentUser)}
          </div>
          <div style={styles.conversationText}>
            {conversation?.text ?? conversation?.items?.[0]?.text}
          </div>
          <div style={styles.footer}>
            <div>
              {conversation?.creation_date
                ? new Date(conversation?.creation_date).toLocaleString()
                : "Just now"}
            </div>
            {conversation?.issue_name && (
              <div style={{...styles.issueNameBadge, marginLeft: "auto"}}>
                {removeIssueGroupPrefix(conversation.issue_name)}
              </div>
            )}
          </div>
        </div>
        <ChevronRightIcon
          style={{color: "rgb(180, 180, 180)", flexShrink: 0}}
        />
      </div>
    </div>
  );
}

function getUsersFromChats(chats: {
  [key: ConversationId]: DocumentConversation;
}) {
  return _.uniqBy(
    Object.values(chats).map(conversation => conversation.first_user),
    "id",
  ).filter(item => item);
}

function renderConversationUsername(conversation, currentUser) {
  if (!conversation) {
    return "";
  }

  if (
    !conversation.first_user ||
    conversation.first_user.id === currentUser.id
  ) {
    return "";
  }

  return getMessageUsername({user: conversation.first_user});
}
